import { Download, Gear } from "@phosphor-icons/react"
import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { MenuButton } from "@tokenterminal/core/ui/menu/MenuButton"
import { type PresetColumnConfig } from "@tokenterminal/tt-analytics-api-types/dist/api/projects"
import { useAuth } from "context/Auth"
import { apiPOSTRaw } from "data/api/post"
import { useAtomValue } from "jotai"
import { sendEvent } from "modules/analytics"
import {
  ACCESS_ACTIONS,
  useAccess,
  ACCESS_COMPONENTS,
} from "modules/auth/useAccess"
import { LazyAuthPaywallModal } from "modules/paywall/LazyAuthPaywallModal"
import { LazyProPaywallModal } from "modules/paywall/LazyProPaywallModal"
import { useState } from "react"
import { type FiltersAtomType } from "./ScreenerTable"
import { fetchProjectArgs } from "./Table"

export default function DownloadAsCSVButton({
  filtersAtom,
  columns,
}: {
  filtersAtom: FiltersAtomType
  columns: PresetColumnConfig[]
}) {
  const hasAccess = useAccess(
    ACCESS_COMPONENTS.TERMINAL,
    ACCESS_ACTIONS.DOWNLOAD
  )

  const filters = useAtomValue(filtersAtom)
  const { isLoggedIn } = useAuth()

  const [isProModalOpen, setIsProModalOpen] = useState(false)

  const handleDownload = async () => {
    if (!hasAccess) {
      sendEvent("wall", {
        action: "download",
        wall_type: "pro",
        component: "master-sheet",
        name: "master-sheet",
        filters: {},
        page_type: "homepage",
      })

      setIsProModalOpen(true)
      return
    }

    const data = await fetchProjectArgs({
      marketSectors: filters.marketSectors,
      chains: filters.chains,
      search: filters.search,
      columns: filters.columns.filter((col) => !col.sparkline),
      sorting: filters.sorting,
    })

    const res = await apiPOSTRaw("/v3/internal/projects/export" as any, {
      columns: data.columns,
      offset: 0,
      limit: 1000,
      sorting: data.sorting,
      filters: data.filters,
      format: "csv",
    })

    if (!res.ok) {
      throw new Error("Failed to download CSV")
    }

    const text = await res.text()
    const blob = new Blob([text], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `token-terminal-projects-${new Date().toISOString().split("T")[0]}.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  return (
    <>
      {isLoggedIn ? (
        <LazyProPaywallModal
          type="download_master_sheet"
          featureName="Downloading the Master sheet"
          onClose={() => setIsProModalOpen(false)}
          isOpen={isProModalOpen}
          setIsOpen={setIsProModalOpen}
        />
      ) : (
        <LazyAuthPaywallModal
          isOpen={isProModalOpen}
          setIsOpen={setIsProModalOpen}
        />
      )}
      <MenuButton
        placement="bottom end"
        trigger={<IconButton icon={Gear} label="Actions" variant="ghost" />}
      >
        <MenuButton.Item onAction={handleDownload} icon={Download}>
          Download as CSV
        </MenuButton.Item>
      </MenuButton>
    </>
  )
}
